import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, Camera, Save, Gift, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AutocompleteInput } from '../components/ui/autocomplete-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { INDIAN_STATES, MAJOR_INDIAN_CITIES } from '../lib/constants';
import PageLoadingState from '../components/PageLoadingState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile() {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    address: {
      address: '',
      city: '',
      state: '',
      pincode: ''
    },
    photo_url: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingPincode, setLoadingPincode] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchLocation = async () => {
      if (profile.address?.pincode?.length === 6 && isEditing) {
        setLoadingPincode(true);
        try {
          const response = await axios.get(`https://api.postalpincode.in/pincode/${profile.address.pincode}`);
          if (response.data[0].Status === 'Success') {
            const { District, State } = response.data[0].PostOffice[0];
            setProfile(prev => ({
              ...prev,
              address: {
                ...prev.address,
                city: District,
                state: State
              }
            }));
          }
        } catch (error) {
          console.error('Failed to fetch location from pincode:', error);
        } finally {
          setLoadingPincode(false);
        }
      }
    };
    fetchLocation();
  }, [profile.address?.pincode, isEditing]);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setProfile({ ...profile, photo_url: response.data.url });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const fetchProfile = async () => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const addr = response.data.address;
      let structuredAddress = { address: '', city: '', state: '', pincode: '' };
      
      if (addr) {
        if (typeof addr === 'object') {
          structuredAddress = {
            address: addr.address || '',
            city: addr.city || '',
            state: addr.state || '',
            pincode: addr.pincode || ''
          };
        } else {
          structuredAddress.address = addr;
        }
      }

      setProfile({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        address: structuredAddress,
        photo_url: response.data.photo_url || '',
        credits: response.data.credits || 0,
        referral_code: response.data.referral_code || ''
      });
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.put(
        `${API}/profile`,
        profile,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local storage with new user data
      const userData = JSON.parse(sessionStorage.getItem('user'));
      const updatedUser = { ...userData, ...response.data };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLoadingState title="Loading your profile" />
    );
  }

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between mb-12">
            <div>
              <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="profile-title">My Profile</h1>
              <p className="text-muted-foreground">Manage your account information</p>
            </div>
            <div className="flex items-center gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/referral">
                  <Button
                    variant="outline"
                    className="rounded-full border-primary/50 text-primary hover:bg-primary/10 relative overflow-hidden group"
                  >
                    <Gift className="h-4 w-4 mr-2" />
                    Refer Now
                    <motion.div
                      className="absolute inset-0 bg-primary/20"
                      initial={{ x: '-100%' }}
                      animate={{ x: '100%' }}
                      transition={{ 
                        repeat: Infinity, 
                        duration: 1.5, 
                        ease: "linear",
                        repeatDelay: 2
                      }}
                    />
                  </Button>
                </Link>
              </motion.div>
              {!isEditing ? (
              <Button
                onClick={() => setIsEditing(true)}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="edit-profile-button"
              >
                Edit Profile
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setIsEditing(false);
                    fetchProfile();
                  }}
                  variant="outline"
                  className="rounded-full"
                  data-testid="cancel-edit-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-full bg-primary hover:bg-primary/90"
                  data-testid="save-profile-button"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}
            </div>
          </div>

          <div className="glass-morphic rounded-2xl p-8 space-y-8">
            {/* Credits Section */}
            <div className="grid md:grid-cols-2 gap-6 pb-6 border-b border-white/10">
              <div className="p-6 rounded-xl bg-primary/10 border border-primary/20">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-full bg-primary/20 text-primary">
                    <Gift className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-lg">My Credits</h3>
                </div>
                <p className="text-3xl font-bold text-primary">₹{profile.credits?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-muted-foreground mt-2">100% of credits can be used for purchases</p>
                {profile.credits >= 1000 && (
                  <Link to="/referral">
                    <Button variant="link" className="p-0 h-auto text-primary mt-2 flex items-center gap-1 text-sm">
                      Encash Now <ExternalLink className="h-3 w-3" />
                    </Button>
                  </Link>
                )}
              </div>
              
              <div className="p-6 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-full bg-white/10 text-muted-foreground">
                    <User className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-lg">Referral Code</h3>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-mono font-bold tracking-wider">{profile.referral_code || '------'}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-full"
                    onClick={() => {
                      navigator.clipboard.writeText(profile.referral_code);
                      toast.success('Referral code copied!');
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Share this code with friends to earn 5% credits on every purchase they make</p>
              </div>
            </div>

            {/* Profile Photo */}
            <div className="flex items-center gap-6">
              <div className="relative">
                {profile.photo_url ? (
                  <img
                    src={getImageUrl(profile.photo_url)}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover border-2 border-primary"
                    data-testid="profile-photo"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary">
                    <User className="h-12 w-12 text-primary" />
                  </div>
                )}
                {isEditing && (
                  <label 
                    htmlFor="file-upload" 
                    className="absolute bottom-0 right-0 bg-primary text-white p-1.5 rounded-full cursor-pointer shadow-lg hover:bg-primary/90 transition-colors"
                  >
                    <Camera className="h-4 w-4" />
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
              {isEditing && (
                <div className="flex-1">
                  <Label htmlFor="photo_url">Photo URL or Upload local image</Label>
                  <div className="flex gap-2">
                    <Input
                      id="photo_url"
                      value={profile.photo_url}
                      onChange={(e) => setProfile({ ...profile, photo_url: e.target.value })}
                      placeholder="https://example.com/photo.jpg"
                      className="bg-white/5 border-white/10"
                      data-testid="photo-url-input"
                    />
                  </div>
                  {uploading && <p className="text-xs text-primary mt-1">Uploading...</p>}
                </div>
              )}
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Name
              </Label>
              <Input
                id="name"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                disabled={!isEditing}
                className="bg-white/5 border-white/10"
                data-testid="name-input"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                disabled={!isEditing}
                className="bg-white/5 border-white/10"
                data-testid="email-input"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Phone Number
              </Label>
              <Input
                id="phone"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                disabled={!isEditing}
                placeholder="Add your phone number"
                className="bg-white/5 border-white/10"
                data-testid="phone-input"
              />
            </div>

            {/* Address */}
            <div className="space-y-4">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Address Details
              </Label>
              
              {!isEditing ? (
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-sm">
                    {profile.address.address || 'No address added'}
                    {(profile.address.city || profile.address.state || profile.address.pincode) && (
                      <>
                        <br />
                        {[profile.address.city, profile.address.state, profile.address.pincode].filter(Boolean).join(', ')}
                      </>
                    )}
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="address">Street Address</Label>
                    <Textarea
                      id="address"
                      value={profile.address.address}
                      onChange={(e) => setProfile({ 
                        ...profile, 
                        address: { ...profile.address, address: e.target.value } 
                      })}
                      placeholder="Street address, apartment, etc."
                      className="bg-white/5 border-white/10"
                      rows={2}
                      data-testid="address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode</Label>
                    <div className="relative">
                      <Input
                        id="pincode"
                        value={profile.address.pincode}
                        onChange={(e) => setProfile({ 
                          ...profile, 
                          address: { ...profile.address, pincode: e.target.value } 
                        })}
                        placeholder="6-digit pincode"
                        className="bg-white/5 border-white/10 pr-10"
                        maxLength={6}
                        data-testid="pincode-input"
                      />
                      {loadingPincode && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <AutocompleteInput
                      id="city"
                      value={profile.address.city}
                      onChange={(e) => setProfile({ 
                        ...profile, 
                        address: { ...profile.address, city: e.target.value } 
                      })}
                      suggestions={MAJOR_INDIAN_CITIES}
                      placeholder="City"
                      className="bg-white/5 border-white/10"
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="state">State</Label>
                    <AutocompleteInput
                      id="state"
                      value={profile.address.state}
                      onChange={(e) => setProfile({ 
                        ...profile, 
                        address: { ...profile.address, state: e.target.value } 
                      })}
                      suggestions={INDIAN_STATES}
                      placeholder="State"
                      className="bg-white/5 border-white/10"
                      data-testid="state-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
